import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { SocketEvents } from '@tcrd/shared';
import { realtime } from '../../lib/realtime';
import { useAuth } from '../../stores/auth';
import { useToast } from '../../components/toast';
import { announceNewRequest } from '../driver/NewRequestAlert';
import { announceCustomerEvent, CUSTOMER_ALERT_TYPES } from '../customer/CustomerEventAlert';
import { announceRideCheck } from '../safety/RideCheckModal';
import { announceReferral } from '../safety/Referral';

/**
 * Conecta Socket.IO cuando hay sesión y traduce eventos del servidor en
 * invalidaciones de TanStack Query (la API sigue siendo la fuente de verdad).
 */
export function RealtimeBridge() {
  const status = useAuth((s) => s.status);
  const qc = useQueryClient();
  const toast = useToast();

  useEffect(() => {
    if (status !== 'authenticated') return;
    realtime.connect();
    const offs = [
      realtime.on(SocketEvents.notification, (p) => {
        void qc.invalidateQueries({ queryKey: ['notifications'] });
        const data = (p.data ?? {}) as Record<string, string>;
        if (p.replayed) {
          // Al volver a la página (iPhone pausa las pestañas en segundo plano): avisar del evento reciente del viaje.
          const recent = typeof p.createdAt === 'string' && Date.now() - new Date(p.createdAt).getTime() < 10 * 60_000;
          if (recent && typeof p.type === 'string' && CUSTOMER_ALERT_TYPES.has(p.type) && typeof p.title === 'string')
            announceCustomerEvent({ title: p.title, body: typeof p.body === 'string' ? p.body : undefined, url: data.url });
          return;
        }
        // Solicitud nueva: aviso grande con sonido en lugar del mensaje pequeño.
        if (p.type === 'ride_request.new' && data.requestId) announceNewRequest(data.requestId);
        // "¿Todo bien?": pregunta de seguridad a pantalla completa.
        // Cliente: "Has sido referido a otro conductor de confianza" (acepta o cancela).
        else if (p.type === 'ride.referred' && data.rideId && data.url?.startsWith('/app/')) announceReferral(data.rideId);
        else if (p.type === 'ride.check' && data.rideId) announceRideCheck(data.rideId, typeof p.body === 'string' ? p.body.replace(' Toca para responder.', '') : undefined);
        // Cliente: aviso visible con sonido (en iPhone no hay push web sin instalar la app).
        else if (typeof p.type === 'string' && CUSTOMER_ALERT_TYPES.has(p.type) && typeof p.title === 'string')
          announceCustomerEvent({ title: p.title, body: typeof p.body === 'string' ? p.body : undefined, url: data.url });
        else if (typeof p.title === 'string') toast(p.title);
      }),
      realtime.on(SocketEvents.rideRequestUpdated, (p) => {
        void qc.invalidateQueries({ queryKey: ['ride-requests'] });
        if (p.requestId) void qc.invalidateQueries({ queryKey: ['ride-request', p.requestId] });
      }),
      realtime.on(SocketEvents.rideUpdated, (p) => {
        void qc.invalidateQueries({ queryKey: ['rides'] });
        if (p.rideId) void qc.invalidateQueries({ queryKey: ['ride', p.rideId] });
      }),
      realtime.on(SocketEvents.chatMessage, (p) => {
        void qc.invalidateQueries({ queryKey: ['conversations'] });
        if (p.conversationId) void qc.invalidateQueries({ queryKey: ['messages', p.conversationId] });
      }),
    ];
    return () => offs.forEach((off) => off());
  }, [status, qc, toast]);

  return null;
}
